/**
 * Public Demo Access — /demo landing.
 *
 * Visual system uses the canonical RestroPulse tokens (white bg,
 * primary #143E30 green, Figtree body, Cabinet Grotesk headings via
 * `<h1>/<h2>/<h3>`). No page-scoped font-family overrides.
 *
 * Pass 3A adds mandatory mobile number + follow-up consent capture and
 * captures UTM/referrer attribution on first visit.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Turnstile widget ─────────────────────────────────────────────────
const TURNSTILE_SCRIPT_ID = 'restropulse-turnstile-script';
function loadTurnstileScript() {
  if (document.getElementById(TURNSTILE_SCRIPT_ID)) return Promise.resolve();
  return new Promise((resolve) => {
    const s = document.createElement('script');
    s.id = TURNSTILE_SCRIPT_ID;
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    document.head.appendChild(s);
  });
}
function TurnstileWidget({ sitekey, onToken, resetKey }) {
  useEffect(() => {
    let id = null, cancelled = false;
    loadTurnstileScript().then(() => {
      if (cancelled) return;
      const tick = () => {
        // eslint-disable-next-line no-undef
        if (window.turnstile && document.getElementById('turnstile-mount')) {
          const el = document.getElementById('turnstile-mount'); el.innerHTML = '';
          // eslint-disable-next-line no-undef
          id = window.turnstile.render('#turnstile-mount', {
            sitekey, callback: onToken,
            'error-callback': () => onToken(''),
            'expired-callback': () => onToken(''),
            theme: 'light',
          });
        } else setTimeout(tick, 120);
      };
      tick();
    });
    return () => {
      cancelled = true;
      // eslint-disable-next-line no-undef
      if (window.turnstile && id) window.turnstile.remove(id);
    };
  }, [sitekey, resetKey, onToken]);
  return <div id="turnstile-mount" data-testid="public-demo-turnstile" />;
}

// ── Country list (short curated set — expand later) ──────────────────
const COUNTRIES = [
  { code: 'IN', dial: '+91',  name: 'India' },
  { code: 'US', dial: '+1',   name: 'United States' },
  { code: 'GB', dial: '+44',  name: 'United Kingdom' },
  { code: 'AE', dial: '+971', name: 'UAE' },
  { code: 'SG', dial: '+65',  name: 'Singapore' },
  { code: 'AU', dial: '+61',  name: 'Australia' },
  { code: 'CA', dial: '+1',   name: 'Canada' },
];

// ── UTM helper ───────────────────────────────────────────────────────
function readAttribution() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source:   p.get('utm_source')   || '',
      utm_medium:   p.get('utm_medium')   || '',
      utm_campaign: p.get('utm_campaign') || '',
      utm_content:  p.get('utm_content')  || '',
      utm_term:     p.get('utm_term')     || '',
      referrer:     document.referrer     || '',
    };
  } catch { return {}; }
}

export default function PublicDemoLanding() {
  const { setPublicDemoSession } = useAuth();
  const [config, setConfig] = useState(null);
  const [persona, setPersona] = useState(null);
  const [step, setStep] = useState('choose');   // choose | form | otp
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('IN');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [otp, setOtp] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [error, setError] = useState(null);
  const attribution = useMemo(readAttribution, []);

  useEffect(() => {
    axios.get(`${API}/public-demo/config`).then(r => setConfig(r.data)).catch(() => setConfig(null));
  }, []);

  const chooseOwner   = () => { setPersona('owner');   setStep('form'); setError(null); };
  const chooseManager = () => { setPersona('manager'); setStep('form'); setError(null); };
  const openGuestWallet = async () => {
    try {
      const res = await axios.post(`${API}/public-demo/guest-wallet-session`, {});
      if (res.data?.session_token) {
        sessionStorage.setItem('loyalty_session_token', res.data.session_token);
      }
      window.location.href = res.data?.redirect_to || '/loyalty/amber-club';
    } catch { window.location.href = '/loyalty/amber-club'; }
  };

  const dial = COUNTRIES.find(c => c.code === country)?.dial || '+91';

  const requestCode = async (e) => {
    e?.preventDefault();
    setError(null);
    if (!email) return setError('Enter your work email to continue.');
    if (!phone || phone.replace(/\D/g, '').length < 6) return setError('Enter a valid mobile number.');
    if (!turnstileToken) return setError('Please complete the human check.');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public-demo/request-code`, {
        email, persona, turnstile_token: turnstileToken,
        phone, phone_country_code: dial, phone_country: country,
        followup_consent: consent,
        attribution,
      });
      if (res.data?.debug_otp) setOtp(res.data.debug_otp);
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send code. Please try again.');
      setResetKey(k => k + 1); setTurnstileToken('');
    } finally { setSubmitting(false); }
  };

  const verifyCode = async (e) => {
    e?.preventDefault();
    setError(null); setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public-demo/verify-code`, { email, persona, otp });
      localStorage.setItem('token', res.data.token);
      localStorage.removeItem('active_outlet_id');
      setPublicDemoSession({
        is_public_demo_session: true,
        demo_persona: res.data.persona,
        demo_session_id: res.data.session_id,
        tenant_id: 'amber_kitchen_group',
        expires_at: res.data.expires_at,
      });
      toast.success('Welcome to the RestroPulse demo.');
      window.location.href = persona === 'owner' ? '/' : '/manager';
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally { setSubmitting(false); }
  };

  const back = () => { setStep('choose'); setPersona(null); setError(null); };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-4 flex items-center gap-3">
          <img src="/restropulse-logo.png" alt="RestroPulse" className="w-9 h-9" />
          <div>
            <div className="text-base font-bold tracking-tight text-[#143E30]"
                  data-testid="public-demo-brand">RestroPulse</div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">
              Live Demo
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-14">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl leading-tight font-semibold"
             style={{ color: 'var(--color-text-primary)' }}
             data-testid="public-demo-headline">
          See how a multi-unit restaurant business looks
          <br className="hidden sm:block" /> when everything is visible.
        </h1>
        <p className="mt-5 max-w-3xl text-slate-600 text-base sm:text-lg">
          Explore The Amber Kitchen Group — five Mumbai restaurant operations
          with realistic finance, compliance, maintenance, loyalty, marketing
          and expansion data.
        </p>

        {step === 'choose' && (
          <section className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
                    data-testid="public-demo-personas">
            <PersonaCard testId="persona-owner"   title="Owner / Admin"
              body="See portfolio performance, outlet comparisons, P&L, operational risks, marketing, loyalty and expansion intelligence."
              cta="Explore as Owner" onClick={chooseOwner} />
            <PersonaCard testId="persona-manager" title="Manager"
              body="Experience outlet operations, checklists, tickets, maintenance and daily workflows."
              cta="Explore as Manager" onClick={chooseManager} />
            <PersonaCard testId="persona-guest"   title="Guest Experience"
              body="See The Amber Club membership card and premium voucher wallet."
              cta="View Amber Club" onClick={openGuestWallet} secondary />
          </section>
        )}

        {step === 'form' && (
          <form onSubmit={requestCode}
                 data-testid="public-demo-email-form"
                 className="mt-10 max-w-xl">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">
              Persona · {persona === 'owner' ? 'Owner / Admin' : 'Manager'}
            </div>
            <Field label="Work email">
              <input type="email" required value={email}
                     onChange={e => setEmail(e.target.value)}
                     placeholder="you@company.com"
                     data-testid="public-demo-email"
                     className={inputClass} />
            </Field>
            <Field label="Mobile number">
              <div className="flex gap-2">
                <select value={country} onChange={e => setCountry(e.target.value)}
                         data-testid="public-demo-country"
                         className={`${inputClass} w-32`}>
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>{c.code} {c.dial}</option>
                  ))}
                </select>
                <input type="tel" required value={phone}
                       onChange={e => setPhone(e.target.value)}
                       placeholder="98765 43210"
                       data-testid="public-demo-phone"
                       className={`${inputClass} flex-1`} />
              </div>
            </Field>
            <label className="mt-4 flex items-start gap-2 text-sm text-slate-700"
                    data-testid="public-demo-consent-label">
              <input type="checkbox" checked={consent}
                     onChange={e => setConsent(e.target.checked)}
                     data-testid="public-demo-consent"
                     className="mt-1 accent-[#143E30]" />
              <span>
                I agree that RestroPulse may contact me by phone regarding this
                demo and its products or services.
              </span>
            </label>
            <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">
              Your details are handled per our{' '}
              <a href="/legal/privacy" target="_blank" rel="noopener noreferrer"
                 data-testid="public-demo-privacy-link"
                 className="underline underline-offset-2 text-[#143E30] hover:text-[#1B5A45]">
                Privacy Policy
              </a>
              {' · '}
              <a href="/legal/terms" target="_blank" rel="noopener noreferrer"
                 data-testid="public-demo-terms-link"
                 className="underline underline-offset-2 text-[#143E30] hover:text-[#1B5A45]">
                Terms
              </a>.
            </p>
            <div className="mt-5">
              {config?.turnstile_sitekey && (
                <TurnstileWidget sitekey={config.turnstile_sitekey}
                                  onToken={setTurnstileToken} resetKey={resetKey} />
              )}
            </div>
            {error && <div className="mt-3 text-sm text-red-600"
                            data-testid="public-demo-error">{error}</div>}
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={back}
                       data-testid="public-demo-back" className={btnSecondary}>
                Back
              </button>
              <button type="submit" disabled={submitting}
                       data-testid="public-demo-send-code" className={btnPrimary}>
                {submitting ? 'Sending…' : 'Send access code'}
              </button>
            </div>
            <p className="mt-5 text-xs text-slate-500 leading-relaxed">
              Your email is used to send your demo access code. If you opt in
              above, our team may also contact you about RestroPulse.
            </p>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={verifyCode} className="mt-10 max-w-md"
                 data-testid="public-demo-otp-form">
            <div className="text-sm text-slate-600">
              We've sent a 6-digit access code to <strong>{email}</strong>
            </div>
            <div className="mt-5">
              <input type="text" inputMode="numeric" pattern="[0-9]{6}"
                     value={otp}
                     onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                     placeholder="000000" maxLength={6} required
                     data-testid="public-demo-otp"
                     className="w-full px-4 py-3 rounded-md border border-slate-300
                                text-2xl tracking-[0.5em] text-center font-mono
                                focus:outline-none focus:ring-2 focus:ring-[#143E30]/30
                                focus:border-[#143E30]" />
            </div>
            {error && <div className="mt-3 text-sm text-red-600"
                            data-testid="public-demo-error">{error}</div>}
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setStep('form')}
                       data-testid="public-demo-otp-back"
                       className={btnSecondary}>Back</button>
              <button type="submit" disabled={submitting || otp.length !== 6}
                       data-testid="public-demo-verify"
                       className={btnPrimary}>
                {submitting ? 'Verifying…' : 'Enter the demo'}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

function PersonaCard({ testId, title, body, cta, onClick, secondary }) {
  return (
    <div data-testid={testId}
          className="rounded-xl border border-slate-200 bg-white p-6
                     shadow-[0_1px_2px_rgba(11,19,16,0.06)]
                     hover:shadow-[0_4px_12px_rgba(11,19,16,0.08)] transition-shadow">
      <h3 className="text-lg font-semibold mb-2"
           style={{ color: 'var(--color-text-primary)' }}>{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed mb-5">{body}</p>
      <button onClick={onClick}
               data-testid={`${testId}-cta`}
               className={secondary ? btnSecondary : btnPrimary}>{cta}</button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mt-4">
      <label className="block text-xs font-medium uppercase tracking-wider
                          text-slate-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

const inputClass =
  'px-3.5 py-2.5 rounded-md border border-slate-300 text-sm text-slate-900 ' +
  'bg-white focus:outline-none focus:ring-2 focus:ring-[#143E30]/30 ' +
  'focus:border-[#143E30]';

const btnPrimary =
  'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md ' +
  'bg-[#143E30] hover:bg-[#1B5A45] text-white text-sm font-medium ' +
  'shadow-sm focus:outline-none focus:ring-2 focus:ring-[#143E30]/30 ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

const btnSecondary =
  'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md ' +
  'bg-white border border-[#143E30] text-[#143E30] hover:bg-[#E7EEEB] ' +
  'text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#143E30]/30';
